import React from "react";
import { Sparkles } from "lucide-react";
import {
  usePrefersReducedMotion,
  useIsMobile,
  useOverrunCopy,
  useHonestProgress,
  genStageLine,
} from "./shared";
import { pick } from "@/lib/wsCopy";
import { INTERVIEW_COPY, pickSourceAware } from "@/lib/interviewCopy";

/** Shared honest progress bar. aria-valuenow = REAL percent; width may drift. */
export function GenProgressBar({ realPercent, done, reduced, stageLine, slim, testId }) {
  const width = useHonestProgress(realPercent, done, reduced);
  return (
    <div className={slim ? "h-1.5 w-full rounded-full bg-lm-tint overflow-hidden"
                         : "h-2.5 w-full rounded-full bg-lm-tint overflow-hidden"}
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(realPercent || 0)}
      aria-valuetext={stageLine}
      data-testid={testId}
    >
      <div
        className={
          "h-full rounded-full bg-gradient-to-r from-lm-primary to-lm-secondary " +
          (reduced ? "" : "transition-[width] duration-200 ease-out ") +
          (done || reduced ? "" : "lm-bar-shimmer")
        }
        style={{ width: `${width}%` }}
      />
    </div>
  );
}

/**
 * The progress strip that sits UNDER the interview questions (Ori flag 3: the
 * questions may not take the space progress held). It has two states and the
 * difference between them is the whole point of this component:
 *
 *   waiting_on_owner === false → real work is in flight (the crawl). Normal
 *       honest bar: real percent anchors it, motion drifts between milestones.
 *
 *   waiting_on_owner === true  → NOTHING is running. Phase 1 finished, Phase 2
 *       cannot start until the owner answers, there is no task and no timer and
 *       therefore no heartbeat. So the bar HOLDS, visibly:
 *         · width is exactly the real percent — no drift toward a next band,
 *           because there is no next band being worked toward
 *         · no shimmer, no transition, no pulse — every animation is removed,
 *           not slowed. A moving pixel here is a claim that something is
 *           happening, and nothing is
 *         · aria-busy="false" and aria-valuetext say "paused", so a screen
 *           reader is told the same thing the sighted user is told
 *       This is the D5 defect class: a screen that looks busy over an idle
 *       server. The fix is not a nicer spinner, it is no spinner.
 */
export function GenWaitStrip({ gen, mobile }) {
  const reduced = usePrefersReducedMotion();
  const overrun = useOverrunCopy(gen, mobile);   // null while waiting — see shared.js
  const waiting = !!gen?.waiting_on_owner;
  // Correct for the pasted-content path, and derived from the job so it
  // survives a refresh.
  const heldLabel = pickSourceAware(pick, gen, INTERVIEW_COPY.progressHeld, INTERVIEW_COPY.progressHeldDump, mobile);
  const percent = gen?.percent || 0;
  const stageLine = genStageLine(gen, mobile);

  if (waiting) {
    return (
      <div className="w-full" data-testid="brand-gen-waiting" data-waiting="true">
        <div className="flex items-center justify-between gap-3 mb-2">
          <span className="inline-flex items-center gap-2 min-w-0 text-sm font-medium text-lm-text2">
            {/* Static. Deliberately NOT animate-pulse — the pulse everywhere
                else in this app means "live", and this is the opposite. */}
            <span className="w-1.5 h-1.5 rounded-full bg-lm-text3 shrink-0" aria-hidden="true" />
            <span className="truncate">{heldLabel}</span>
          </span>
          <span className="shrink-0 text-sm font-mono font-semibold text-lm-text3 tabular-nums">
            {Math.round(percent)}%
          </span>
        </div>
        <div
          className="h-2.5 w-full rounded-full bg-lm-tint overflow-hidden"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(percent)}
          aria-valuetext={`${heldLabel}. Paused.`}
          aria-busy="false"
          data-testid="brand-gen-waiting-progress"
        >
          {/* Muted fill, no gradient sheen, no transition class at all: this bar
              cannot move, so it must not be built out of things that move. */}
          <div className="h-full rounded-full bg-lm-primary/40" style={{ width: `${percent}%` }} />
        </div>
      </div>
    );
  }

  return (
    <div className="w-full" data-testid="brand-gen-waiting" data-waiting="false">
      <div className="flex items-center justify-between gap-3 mb-2">
        <span
          key={overrun ? "reassure" : stageLine}
          className={`text-sm font-medium min-w-0 truncate ${overrun ? "text-lm-secondary" : "text-lm-text2"} ${reduced ? "" : "animate-[fadeIn_0.3s_ease]"}`}
        >
          {overrun || stageLine}
        </span>
        <span className="shrink-0 text-sm font-mono font-semibold text-lm-primary tabular-nums">
          {Math.round(percent)}%
        </span>
      </div>
      <GenProgressBar
        realPercent={percent}
        done={false}
        reduced={reduced}
        stageLine={overrun || stageLine}
        testId="brand-gen-interview-progress"
      />
    </div>
  );
}

/**
 * Full brand-generation loader shown BEFORE the profile lands (~0–20%). Once the
 * profile arrives the parent switches to the review step and this unmounts,
 * replaced by the slim GenRibbon at the top of the review.
 */
export function BrandGenLoader({ gen, onRetry, notified, onNotify }) {
  const reduced = usePrefersReducedMotion();
  const mobile = useIsMobile();
  const overrun = useOverrunCopy(gen, mobile);
  const error = gen?.status === "error";
  const percent = gen?.percent || 0;
  const stageLine = genStageLine(gen, mobile);

  if (error) {
    return (
      <div className="w-full max-w-md" data-testid="brand-gen-error">
        <p className="text-sm font-medium text-lm-text">
          {mobile
            ? "That didn't finish — I saved your details. Try again?"
            : "That didn't finish — but I kept your site, so nothing's lost. One tap to try again."}
        </p>
        <button
          type="button"
          onClick={onRetry}
          data-testid="brand-gen-retry"
          className="mt-3 inline-flex items-center gap-2 bg-lm-primary text-white rounded-lg px-5 py-2.5 min-h-[44px] font-semibold hover:bg-lm-primaryHover transition-colors"
        >
          <Sparkles className="w-4 h-4" /> Try again
        </button>
      </div>
    );
  }

  return (
    <div className="w-full max-w-md" data-testid="brand-gen-loader">
      <p className="text-sm font-medium text-lm-text">
        {mobile
          ? "This takes about 2–4 minutes — grab a coffee ☕. I'm reading your whole site and building your brand, ideal customers, and buyer keywords. Each piece appears the moment it's ready."
          : "This takes about 2–4 minutes — so grab a coffee ☕. I'm reading your whole site in depth and building your brand, ideal customers, and buyer keywords. I'll reveal each piece the moment it's ready, so you can start reviewing while I finish the rest."}
      </p>
      <div className="flex items-center justify-between mb-2 mt-4">
        <span
          key={overrun ? "reassure" : stageLine}
          className={`text-sm font-medium ${overrun ? "text-lm-secondary" : "text-lm-text2"} ${reduced ? "" : "animate-[fadeIn_0.3s_ease]"}`}
        >
          {overrun || stageLine}
        </span>
        <span className="text-sm font-mono font-semibold text-lm-primary tabular-nums">
          {Math.round(percent)}%
        </span>
      </div>
      <GenProgressBar
        realPercent={percent}
        done={false}
        reduced={reduced}
        stageLine={overrun || stageLine}
        testId="brand-gen-progress"
      />

      {gen?.job_id && (
        <div className="mt-4">
          {notified ? (
            <p className="text-sm text-lm-text3" data-testid="brand-gen-notified">
              ✓ Done — I'll email you the moment it's ready. Feel free to close this tab.
            </p>
          ) : (
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-sm text-lm-text2">Taking a while? We'll email you the moment it's ready.</span>
              <button
                type="button"
                data-testid="brand-gen-notify"
                onClick={onNotify}
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-white bg-lm-primary rounded-lg px-3.5 py-1.5 hover:bg-lm-primaryHover transition-colors shrink-0"
              >
                Notify me
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Slim, sticky status ribbon shown at the top of the review while the rest of
 * the brand still streams. Stays visible as the user edits cards below; it is
 * removed the moment the job is done (that's also when Confirm enables).
 */
export function GenRibbon({ gen, onRetry, notified, onNotify }) {
  const reduced = usePrefersReducedMotion();
  const mobile = useIsMobile();
  const overrun = useOverrunCopy(gen, mobile);
  const error = gen?.status === "error";
  const percent = gen?.percent || 0;
  const stageLine = genStageLine(gen, mobile);

  return (
    <div
      className="sticky top-0 z-30 -mx-4 md:-mx-5 px-4 md:px-5 py-2.5 mb-4 bg-lm-surface/95 backdrop-blur border-b border-lm-border"
      data-testid="brand-gen-ribbon"
    >
      {error ? (
        <div className="flex items-center justify-between gap-3">
          <span className="text-sm text-lm-secondary font-medium min-w-0 truncate">
            {mobile ? "That didn't finish — I saved your details." : "That didn't finish — nothing's lost."}
          </span>
          <button
            type="button"
            onClick={onRetry}
            data-testid="brand-gen-ribbon-retry"
            className="shrink-0 inline-flex items-center gap-1.5 bg-lm-primary text-white rounded-lg px-3.5 py-2 min-h-[44px] text-sm font-semibold hover:bg-lm-primaryHover"
          >
            Try again
          </button>
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between gap-3 mb-1.5">
            <span
              key={overrun ? "reassure" : stageLine}
              className={`text-xs font-medium min-w-0 truncate ${overrun ? "text-lm-secondary" : "text-lm-text2"} ${reduced ? "" : "animate-[fadeIn_0.3s_ease]"}`}
            >
              {overrun || stageLine}
            </span>
            <span className="shrink-0 text-xs font-mono font-semibold text-lm-primary tabular-nums">
              {Math.round(percent)}%
            </span>
          </div>
          <GenProgressBar
            realPercent={percent}
            done={false}
            reduced={reduced}
            stageLine={overrun || stageLine}
            slim
            testId="brand-gen-ribbon-progress"
          />
          {gen?.job_id && (
            <div className="mt-1.5">
              {notified ? (
                <p className="text-xs text-lm-text3" data-testid="brand-gen-ribbon-notified">
                  ✓ I'll email you the moment it's ready. Feel free to close this tab.
                </p>
              ) : (
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs text-lm-text2">Taking a while? We'll email you when it's ready.</span>
                  <button
                    type="button"
                    data-testid="brand-gen-ribbon-notify"
                    onClick={onNotify}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-white bg-lm-primary rounded-md px-2.5 py-1 hover:bg-lm-primaryHover transition-colors shrink-0"
                  >
                    Notify me
                  </button>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
